import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Observable, race, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { EAuthActions, Login, LoginFailure, LoginSuccess } from '../store/actions/auth.actions';
import { AppState } from '../store/states/app.state';
import { AuthService } from './auth.service';
import {
  LoginCredentials,
  LoginFlowError,
  MfaApiCode,
  MfaChallengeResult,
  MfaVerifyResult
} from './login-flow.models';
import { executeRecaptcha } from './recaptcha.helpers';
import { logAuthError, logAuthWarn } from './auth-debug.helpers';

/**
 * Login option A: password → MFA challenge → OAuth session.
 */
@Injectable({ providedIn: 'root' })
export class LoginFlowService {
  private activeChallenge: string | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly recaptcha: ReCaptchaV3Service,
    private readonly store: Store<AppState>,
    private readonly actions$: Actions
  ) {}

  runRecaptcha(action = 'submitForm'): Observable<string> {
    return executeRecaptcha(this.recaptcha, action).pipe(
      catchError((e) => {
        logAuthError('login-flow: recaptcha failed', e, { action });
        return throwError(() => new LoginFlowError('RECAPTCHA'));
      })
    );
  }

  /**
   * Step 1 — Validate email + password on server; receive MFA challenge.
   */
  startPrimaryAuth(
    credentials: LoginCredentials,
    captchaToken: string
  ): Observable<MfaChallengeResult> {
    return this.authService
      .startLogin(credentials.email, credentials.password, captchaToken)
      .pipe(
        map(response => {
          if (response.code === MfaApiCode.Verified && response.mfa_required && response.challenge) {
            return response;
          }
          if (
            response.code === MfaApiCode.Forbidden ||
            response.error === 'user_inactive'
          ) {
            logAuthWarn('login-flow: user inactive', {
              code: response.code,
              error: response.error
            });
            throw new LoginFlowError(
              'USER_INACTIVE',
              response.message || 'Usuario inactivo'
            );
          }
          if (response.code === MfaApiCode.Unauthorized) {
            logAuthWarn('login-flow: invalid credentials', { code: response.code });
            throw new LoginFlowError(
              'MFA_CHECK_FAILED',
              response.message || 'Credenciales inválidas'
            );
          }
          logAuthWarn('login-flow: startPrimaryAuth unexpected response', {
            code: response.code,
            message: response.message
          });
          throw new LoginFlowError(
            'MFA_CHECK_FAILED',
            response.message || 'No se pudo iniciar la sesión'
          );
        }),
        tap(response => {
          this.activeChallenge = response.challenge;
        }),
        map(response => ({
          challenge: response.challenge,
          qrCodeUrl: response.qr_code,
          requiresQrSetup: !!response.requires_qr_setup
        })),
        catchError((e) => {
          if (!(e instanceof LoginFlowError)) {
            logAuthError('login-flow: startPrimaryAuth HTTP error', e);
          }
          return throwError(() => e);
        })
      );
  }

  /**
   * Step 2 — Verify TOTP (password was validated in step 1).
   */
  verifyTwoFactor(
    otp: string,
    captchaToken: string
  ): Observable<MfaVerifyResult> {
    const challenge = this.activeChallenge;
    if (!challenge) {
      logAuthWarn('login-flow: verifyTwoFactor without active challenge');
      return throwError(
        () =>
          new LoginFlowError(
            'MFA_CHECK_FAILED',
            'Sesión de verificación expirada. Vuelva a iniciar sesión.'
          )
      );
    }

    const code = (otp || '').replace(/\D/g, '');
    if (code.length !== 6) {
      logAuthWarn('login-flow: invalid OTP length', { length: code.length });
      return throwError(
        () => new LoginFlowError('INVALID_OTP', 'Código inválido')
      );
    }

    return this.authService.completeLoginMfa(challenge, code, captchaToken).pipe(
      map(response => {
        if (response.code === MfaApiCode.Verified) {
          this.activeChallenge = null;
          return { passwordExpired: !!response.password_expired };
        }
        if (response.code === MfaApiCode.Forbidden) {
          logAuthWarn('login-flow: MFA forbidden', { message: response.message });
          throw new LoginFlowError('AUTH_DENIED', response.message);
        }
        logAuthWarn('login-flow: invalid OTP', { code: response.code, message: response.message });
        throw new LoginFlowError(
          'INVALID_OTP',
          response.message || 'Código inválido'
        );
      }),
      catchError((e) => {
        if (!(e instanceof LoginFlowError)) {
          logAuthError('login-flow: verifyTwoFactor HTTP error', e);
        }
        return throwError(() => e);
      })
    );
  }

  /**
   * Step 3 — OAuth token (allowed only after MFA gate on backend).
   */
  completeOAuthLogin(
    credentials: LoginCredentials,
    captchaToken: string
  ): Observable<void> {
    this.store.dispatch(
      new Login({
        username: credentials.email,
        password: credentials.password,
        token: captchaToken
      })
    );

    return race(
      this.actions$.pipe(
        ofType<LoginSuccess>(EAuthActions.LoginSuccess),
        take(1),
        map(() => undefined)
      ),
      this.actions$.pipe(
        ofType<LoginFailure>(EAuthActions.LoginFailure),
        take(1),
        switchMap(() => {
          logAuthWarn('login-flow: OAuth login failed (LoginFailure action)');
          return throwError(() => new LoginFlowError('OAUTH_FAILED'));
        })
      )
    );
  }

  updateExpiredPassword(
    credentials: LoginCredentials,
    newPassword: string,
    captchaToken: string
  ): Observable<string> {
    return this.authService
      .updatePassword(
        credentials.email,
        credentials.password,
        newPassword,
        captchaToken
      )
      .pipe(
        map(response => {
          if (response.code === MfaApiCode.Verified && response.password) {
            return response.password;
          }
          logAuthWarn('login-flow: updateExpiredPassword failed', {
            code: response.code
          });
          throw new LoginFlowError(
            'PASSWORD_UPDATE_FAILED',
            'Password could not be updated'
          );
        }),
        catchError((e) => {
          if (!(e instanceof LoginFlowError)) {
            logAuthError('login-flow: updateExpiredPassword HTTP error', e);
          }
          return throwError(() => e);
        })
      );
  }

  resetFlow(): void {
    this.activeChallenge = null;
  }
}
